import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup } from '@angular/forms';
import {
  FileInputModel,
  FileInputValueModel,
  InputItemModel,
  InputListModel,
  InputValueType,
  isFileInput,
  isInputItem,
  isInputListItem,
  MultiStepFormItemModel,
  PreviewItemModel,
  StepItemModel,
} from '@libs/form/models';
import { ViewportService } from '@libs/shared/services';
import { debounceTime, EMPTY, map, Observable, startWith, switchMap } from 'rxjs';
import { ItemBaseComponent } from '../../../models';
import { FormConfigService, ValueFormatterService } from '../../../services';
import { FilePreviewListComponent } from '../../input';
import {
  FieldPreviewModel,
  FileFieldPreviewModel,
  InputListPreviewModel,
  StepPreviewModel,
  TableColumnModel,
  TableRowModel,
} from './preview.model';

@Component({
  selector: 'ui-form-preview',
  templateUrl: './preview.component.html',
  styleUrls: ['./preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AsyncPipe,
    FilePreviewListComponent,
  ],
})
export class PreviewComponent extends ItemBaseComponent implements OnInit {
  private readonly formConfigService: FormConfigService = inject(FormConfigService);
  private readonly valueFormatter: ValueFormatterService = inject(ValueFormatterService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly viewportService: ViewportService = inject(ViewportService);

  @Input() public data!: PreviewItemModel;

  public isMobile$: Observable<boolean> = EMPTY;
  public preview$: Observable<StepPreviewModel[]> = EMPTY;
  public readonly emptyValueMessage: string = 'keine Angabe';

  public ngOnInit(): void {
    this.isMobile$ = this.viewportService.isMobile();

    this.preview$ = this.formConfigService.getConfig().pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(config => {
        const valueChanges$: Observable<unknown> = config.control.valueChanges.pipe(debounceTime(500), startWith(null));

        return valueChanges$.pipe(
          map(() => this.createPreview(config.form, config.control)),
        );
      }),
    );
  }

  private createPreview(form: MultiStepFormItemModel, control: FormGroup): StepPreviewModel[] {
    return form.items
      .filter(item => !item.hideInPreview)
      .map(item => this.createStepPreview(item, control.get(item.name) as FormGroup));
  }

  private createStepPreview(stepItem: StepItemModel, stepControl: FormGroup): StepPreviewModel {
    const fields: FieldPreviewModel[] = [];
    const files: FileFieldPreviewModel[] = [];
    const inputList: InputListPreviewModel[] = [];

    for (const item of stepItem.items) {
      if (!isInputItem(item)) {
        continue;
      }

      const control: AbstractControl | null = stepControl.get(item.name);

      if (!control || control.disabled) {
        continue;
      }

      // Handle input list items
      if (isInputListItem(item)) {
        inputList.push(this.createInputListPreview(item, control as FormGroup));

        continue;
      }

      if (isFileInput(item)) {
        files.push(this.createFileFieldPreview(item, control));

        continue;
      }

      fields.push(this.createFieldPreview(item, control));
    }

    return {
      label: stepItem.title ?? stepItem.name,
      fields,
      files,
      inputList,
    };
  }

  private createFieldPreview(item: InputItemModel, control: AbstractControl): FieldPreviewModel {
    const value: InputValueType = control.value as InputValueType;

    return {
      label: this.formatFieldLabel(item),
      value: this.valueFormatter.formatValue(item, value),
    };
  }

  private createFileFieldPreview(item: FileInputModel, control: AbstractControl): FileFieldPreviewModel {
    return {
      label: this.formatFieldLabel(item),
      value: Array.isArray(control.value)
        ? control.value as FileInputValueModel[]
        : undefined,
    };
  }

  private createInputListPreview(listItem: InputListModel, formGroup: FormGroup): InputListPreviewModel {
    // Map input items to table column definitions with labels and names
    const columns: TableColumnModel[] = listItem.items.map(item => ({
      label: item.label ?? item.name,
      name: item.name,
    }));

    // Build table rows by iterating through form controls (each control represents one row)
    const rows: TableRowModel[] = Object.keys(formGroup.controls).map(rowKey => {
      const rowControl: FormGroup = formGroup.get(rowKey) as FormGroup;
      const rowData: TableRowModel = {};

      // Populate each cell with formatted values from the corresponding input controls
      listItem.items.forEach(item => {
        const cellControl: AbstractControl | null = rowControl.get(item.name);
        const cellValue: string[] | undefined = this.valueFormatter.formatValue(item, cellControl?.value);

        rowData[item.name] = cellValue?.join(', ') ?? this.emptyValueMessage;
      });

      return rowData;
    });

    return {
      label: listItem.label ?? listItem.name,
      columns,
      rows,
    };
  }

  private formatFieldLabel(item: InputItemModel): string {
    return item.labelInfo ?? item.label ?? item.name;
  }
}
